import { useCallback, useEffect, useRef, useState } from "react";
// Components
import TextInput from "view/pages/MyProfile/Components/Inputs/TextInput";
import { FileTab } from "../Tab";
import { Toasts } from "view/components/Toasts";
// Assets
import VideoImage from "assets/images/Placeholders/Video.png";
import AudioImage from "assets/images/Placeholders/Audio.png";
import TextImage from "assets/images/Placeholders/Pdf.png";
// Libs
import { useDropzone } from "react-dropzone";
import { TailSpin } from "react-loader-spinner";
// Services
import apiLibrary from "services/api";
import useCustomBranding from "hooks/useCustomBranding";

type AttachImageCardProps = {
  handleClose: (resetState?: boolean) => void;
  values: any;
  handleChange: any;
  handleBlur: any;
  errors: any;
  touched: any;
  setFieldValue: any;
  isLoading: any;
  setIsLoading: any
};

export const AttachImageCard = ({
  handleClose,
  values,
  handleChange,
  handleBlur,
  errors,
  touched,
  setFieldValue,
  isLoading,
  setIsLoading
}: AttachImageCardProps) => {
  const [selectedTab, setSelectedTab] = useState("add_file");
  const [image, setImage] = useState<any>(null);
  const [fileName, setFileName] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { primaryColor } = useCustomBranding();

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const MAX_IMAGE_WIDTH = 2048;
      const MAX_IMAGE_HEIGHT = 1152;

      if (!acceptedFiles?.length) {
        Toasts.error("No file selected.");
        return;
      }

      const file = acceptedFiles[0];
      const fileType = file.type;
      // Determine size limit by file type
      let maxSizeMB = 100; // default for documents
      if (fileType.startsWith("video/")) {
        maxSizeMB = 200;
      } else if (
        fileType.startsWith("image/") ||
        fileType.startsWith("audio/")
      ) {
        maxSizeMB = 25;
      }

      if (file.size > maxSizeMB * 1024 * 1024) {
        Toasts.error(`File size must be less than ${maxSizeMB} MB!`);
        setImage(null);
        return;
      }

      const isImage = fileType.startsWith("image/");

      if (isImage) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const img = new Image();
          img.onload = async () => {
            if (img.width > MAX_IMAGE_WIDTH || img.height > MAX_IMAGE_HEIGHT) {
              Toasts.error(
                `Image dimensions must be less than ${MAX_IMAGE_WIDTH} x ${MAX_IMAGE_HEIGHT} pixels.`
              );
              setImage(null);
              return;
            }

            try {
              setIsLoading(true);
              setImage(e.target?.result);
              const uploadedResponse = await apiLibrary.file.fileUpload(file);
              setFileName(file?.name);
              setFieldValue("fileId", uploadedResponse?.data?.id);
            } catch (err) {
              Toasts.error("Upload failed. Please try again.");
              setImage(null);
            } finally {
              setIsLoading(false);
            }
          };

          img.onerror = () => {
            Toasts.error("Invalid image file.");
            setImage(null);
          };

          if (e.target?.result) {
            img.src = e.target.result as string;
          }
        };

        reader.onerror = () => {
          Toasts.error("Failed to read the file.");
        };

        reader.readAsDataURL(file);
      } else {
        const isVideo = fileType.startsWith("video/");
        const isAudio = fileType.startsWith("audio/");

        try {
          setIsLoading(true);
          if (isVideo) {
            setImage(VideoImage);
          } else if (isAudio) {
            setImage(AudioImage);
          } else {
            setImage(TextImage);
          }

          setFileName(file?.name);
          const uploadedResponse = await apiLibrary.file.fileUpload(file);
          setFieldValue("fileId", uploadedResponse?.data?.id);
        } catch (err) {
          Toasts.error("Upload failed. Please try again.");
        } finally {
          setIsLoading(false);
        }
      }
    },
    [setFieldValue]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      "application/pdf": [".pdf"],
      "image/png": [".png"],
      "image/jpeg": [".jpg", ".jpeg"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        [".docx"],
      "application/msword": [".doc"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
      "application/vnd.openxmlformats-officedocument.presentationml.presentation":
        [".pptx"],
      "text/plain": [".txt"],
      "text/csv": [".csv"],
      "audio/mpeg": [".mp3"],
      "audio/wav": [".wav"],
      "audio/x-m4a": [".m4a"],
      "video/mp4": [".mp4"],
      "video/x-msvideo": [".avi"],
    },
    maxFiles: 1,
  });

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  useEffect(() => {
    if (selectedTab === "add_file") {
      setFieldValue("link", "");
    }
    if (selectedTab === "add_url") {
      setFieldValue("fileId", "");
      setImage(null);
    }
  }, [selectedTab]);
  return (
    <div className="py-4 px-6 bg-white overflow-y-auto w-full">
      <div className="relative flex items-center self-stretch justify-start flex-grow-0 flex-shrink-0 gap-4 py-2 rounded-lg mb-3">
        <button onClick={() => handleClose(true)}>
          {" "}
          <svg
            width={24}
            height={24}
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="relative flex-grow-0 flex-shrink-0 w-6 h-6"
            preserveAspectRatio="xMidYMid meet"
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M5.29289 5.29289C5.68342 4.90237 6.31658 4.90237 6.70711 5.29289L12 10.5858L17.2929 5.29289C17.6834 4.90237 18.3166 4.90237 18.7071 5.29289C19.0976 5.68342 19.0976 6.31658 18.7071 6.70711L13.4142 12L18.7071 17.2929C19.0976 17.6834 19.0976 18.3166 18.7071 18.7071C18.3166 19.0976 17.6834 19.0976 17.2929 18.7071L12 13.4142L6.70711 18.7071C6.31658 19.0976 5.68342 19.0976 5.29289 18.7071C4.90237 18.3166 4.90237 17.6834 5.29289 17.2929L10.5858 12L5.29289 6.70711C4.90237 6.31658 4.90237 5.68342 5.29289 5.29289Z"
              className="dark:fill-textMain fill-[#2C3236]"
            />
          </svg>
        </button>
        <div className="flex items-center justify-start flex-grow">
          <div className="flex justify-start items-center text-secondaryMid flex-grow relative py-0.5">
            <p className="flex-grow-0 flex-shrink-0 text-sm font-medium text-center capitalize text-textMid dark:text-textMain">
              Attach File
            </p>
          </div>
        </div>
      </div>

      {/* File and URl tabs */}
      <FileTab onChange={(tab) => setSelectedTab(tab)} />

      {selectedTab === "add_file" && (
        <>
          <div
            {...getRootProps()}
            onClick={handleClick}
            className="flex flex-col items-center self-stretch justify-center flex-grow px-3 py-2 bg-white border border-dashed rounded-lg cursor-pointer border-lineDark min-h-[200px]"
          >
            <div className="flex flex-col items-center self-stretch justify-start flex-grow-0 flex-shrink-0">
              <div className="relative flex items-center self-stretch justify-center flex-grow-0 flex-shrink-0 gap-1 py-1 rounded-lg">
                <input {...getInputProps()} ref={fileInputRef} />
                {isLoading ? (
                  <>
                    <div className="flex items-center justify-center w-full loader">
                      <TailSpin
                        height="30"
                        width="30"
                        color={primaryColor}
                        ariaLabel="tail-spin-loading"
                        radius="2"
                        wrapperStyle={{}}
                        wrapperClass="tailspin-loader"
                        visible={true}
                      />
                    </div>
                  </>
                ) : image ? (
                  <div className="w-full text-center">
                    <div className="w-full rounded-lg flex items-center justify-center">
                      <img
                        src={image}
                        alt="Uploaded"
                        className="w-full h-auto max-h-[170px] rounded-lg object-scale-down"
                      />
                    </div>
                    <p className="text-sm text-textMid dark:text-caption break-words">
                      {fileName}
                    </p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center flex-grow-0 flex-shrink-0 gap-1">
                    <svg
                      width={24}
                      height={24}
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="relative flex-grow-0 flex-shrink-0 w-6 h-6"
                      preserveAspectRatio="xMidYMid meet"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M11 3C8.79086 3 7 4.79086 7 7C7 7.30288 7.03348 7.59674 7.09656 7.87852C7.21716 8.41729 6.8783 8.95187 6.33958 9.07271C5.00015 9.37316 4 10.571 4 12C4 13.6569 5.34315 15 7 15C7.55228 15 8 15.4477 8 16C8 16.5523 7.55228 17 7 17C4.23858 17 2 14.7614 2 12C2 9.94357 3.24073 8.1787 5.01385 7.41034C5.00466 7.27465 5 7.13781 5 7C5 3.68629 7.68629 1 11 1C13.627 1 15.858 2.68745 16.6713 5.03713C19.669 5.37085 22 7.91321 22 11C22 13.9039 19.938 16.324 17.1989 16.88C16.6577 16.9899 16.1299 16.6402 16.02 16.0989C15.9101 15.5577 16.2598 15.0298 16.8011 14.92C18.6264 14.5494 20 12.934 20 11C20 8.79086 18.2091 7 16 7C15.9732 7 15.9465 7.00026 15.9198 7.00079C15.4368 7.01024 15.0161 6.67312 14.9201 6.19971C14.5499 4.37395 12.9343 3 11 3ZM11.2929 9.29289C11.6834 8.90237 12.3166 8.90237 12.7071 9.29289L15.7071 12.2929C16.0976 12.6834 16.0976 13.3166 15.7071 13.7071C15.3166 14.0976 14.6834 14.0976 14.2929 13.7071L13 12.4142L13 22C13 22.5523 12.5523 23 12 23C11.4477 23 11 22.5523 11 22L11 12.4142L9.70711 13.7071C9.31658 14.0976 8.68342 14.0976 8.29289 13.7071C7.90237 13.3166 7.90237 12.6834 8.29289 12.2929L11.2929 9.29289Z"
                        className="fill-secondary dark:fill-caption"
                      />
                    </svg>
                    <p className="flex-grow w-[260px] text-sm text-center text-textMid dark:text-caption">
                      Drag and drop your file here or click in this area
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {selectedTab === "add_url" && (
        <div className="w-full pt-2">
          <TextInput
            label=""
            type="text"
            placeholder="Enter URL"
            name="link"
            handleChange={handleChange}
            setFieldValue={setFieldValue}
            handleBlur={handleBlur}
            value={values.link}
            error={errors?.link}
            touched={touched.link}
          />
        </div>
      )}
    </div>
  );
};
